import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import SelectField from './SelectField';
import { InlineSpinner } from './LoadingState';
import { Link, useNavigate } from 'react-router-dom';
import {
  FaSearch,
  FaTimes,
  FaSave,
  FaChevronDown
} from 'react-icons/fa';
import { Formatter } from '../format';
import { Placeholder } from './Placeholder';
import { SentimentIcon } from './SentimentIcon';
import { TrendIcon } from './TrendIcon';
import { useLocalStorage, useSessionStorage } from '../common/localStorage';
import { override } from '../api/api';
import { qaScoreOf } from '../common/assessment';

export const PAGE_SIZE_OPTIONS = [10, 20, 50];

// Default table preferences
export const DEFAULT_PREFERENCES = {
  pageSize: 10,
  sortField: 'timestamp',
  sortDirection: 'desc',
  visibleColumns: [],
  wrapLines: false,
  stripedRows: false
};

// Column definitions for the contact table
const BASE_COLUMNS = [
  {
    id: "timestamp",
    header: "Timestamp",
    sortingField: "timestamp",
    cell: (d) => {
      if (d.status !== undefined && d.status.length > 0 && d.status !== "Done") {
        return Formatter.Timestamp(d.timestamp);
      } else {
        return (
          <Link
            to={`/dashboard/${d.key || d.filename}`}
            onClick={(e) => e.stopPropagation()}
            className="text-cloudza-600 hover:text-cloudza-800 hover:underline transition-colors"
          >
            {Formatter.Timestamp(d.timestamp)}
          </Link>
        );
      }
    },
    minWidth: 160
  },
  {
    id: "jobName",
    header: "Job Name",
    cell: (d) => {
      if (d.status !== undefined && d.status.length > 0 && d.status !== "Done") {
        return d.jobName;
      } else {
        // Matches the CampaignDashboard calls-table pattern: short mono GUID
        // plus a "View" label, with the full value in the tooltip.
        return (
          <Link
            to={`/dashboard/${d.key || d.filename}`}
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1.5 text-cloudza-600 font-medium hover:text-cloudza-800 hover:underline"
            title={`${d.guid} - ${d.timestamp}`}
          >
            <span className="font-mono text-xs">{String(d.guid || '').slice(0, 8)}…</span>
            View
          </Link>
        );
      }
    },
    isRowHeader: true,
    sortingField: "jobName",
  },
  {
    id: "assignedto",
    header: "Assigned To",
    cell: (d) => d.assignedto || d.assignedTo || '—',
    sortingField: "assignedto",
    minWidth: 150,
  },
  {
    id: "assessed",
    header: "Assessed",
    cell: (d) => {
      if (d.assessed === true) {
        return (
          <span className="status-badge status-pass">
            Assessed
          </span>
        );
      }
      return (
        <span className="status-badge status-not-started">
          Not Assessed
        </span>
      );
    },
    sortingField: "assessed",
    minWidth: 130,
  },
  {
    id: "disputed",
    header: "Disputed",
    cell: (d) => {
      if (d.disputed !== true) {
        return <span className="text-gray-400">—</span>;
      }
      if (d.disputeStatus === "resolved") {
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${d.disputeOutcome === "changed" ? "bg-blue-100 text-blue-800" : "bg-green-100 text-green-800"}`}>
            Resolved &mdash; {d.disputeOutcome === "changed" ? "Changed" : "Upheld"}
          </span>
        );
      }
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          Disputed
        </span>
      );
    },
    sortingField: "disputeStatus",
    minWidth: 140,
  },
  {
    // The three assessment outcomes sit together: QA Score, Compliance, Conduct.
    // Their data lives under d.Result.*, so they carry dotted sortingFields that the
    // sort comparator resolves via a nested value getter.
    id: "qaScore",
    header: "QA Score",
    sortingField: "Result.QA_Score",
    cell: (d) => {
      // A compliance-only scorecard has no QA score; its stored 0 is not one.
      const score = qaScoreOf(d);
      if (score === null) {
        return <span className="text-gray-400">—</span>;
      }
      // Matches the pass/marginal/fail banding used for the rating letter on the Assigned page.
      const tone = score < 40 ? "status-fail"
        : score > 59 ? "status-pass"
        : "status-medium";
      return (
        <span className={`status-badge ${tone}`}>
          {score}%
        </span>
      );
    },
    minWidth: 110,
  },
  {
    id: "compliance",
    header: "Compliance",
    sortingField: "Result.Compliance",
    cell: (d) => {
      if (d.Result?.Compliance === false) {
        return (
          <span className="status-badge status-fail">
            Fail
          </span>
        );
      }
      if (d.Result?.Compliance === true) {
        return (
          <span className="status-badge status-pass">
            Pass
          </span>
        );
      }
      return <span className="text-gray-400">—</span>;
    },
    minWidth: 120,
  },
  {
    // Agent Behavior and Conduct verdict. Absent means the call predates the category, or its
    // scorecard asked no conduct questions - shown as a dash rather than a pass, since nothing
    // was assessed.
    id: "conduct",
    header: "Conduct",
    sortingField: "Result.Conduct",
    cell: (d) => {
      if (d.Result?.Conduct === false) {
        return (
          <span className="status-badge status-fail">
            Fail
          </span>
        );
      }
      if (d.Result?.Conduct === true) {
        return (
          <span className="status-badge status-pass">
            Pass
          </span>
        );
      }
      return <span className="text-gray-400">—</span>;
    },
    minWidth: 110,
  },
  {
    id: "guid",
    header: "GUID",
    cell: (d) => (
      <span className="font-mono text-sm">{d.guid}</span>
    ),
    isRowHeader: true,
    sortingField: "guid",
    minWidth: 150
  },
  {
    id: "conversationId",
    header: "Conversation ID",
    cell: (d) => (
      <span className="font-mono text-sm">{d.conversationId || '—'}</span>
    ),
    sortingField: "conversationId",
    minWidth: 160
  },
  {
    id: "dnis",
    header: "DNIS",
    cell: (d) => (
      <span className="font-mono text-sm">{d.dnis || '—'}</span>
    ),
    sortingField: "dnis",
    minWidth: 140
  },
  {
    id: "agent",
    header: "Agent",
    cell: (d) => d.agent,
    isRowHeader: true,
    sortingField: "agent",
    minWidth: 130
  },
  {
    id: "customer",
    header: "Campaign",
    cell: (d) => d.customer,
    isRowHeader: true,
    sortingField: "customer",
    minWidth: 130
  },
  // {
  //   id: "queue",
  //   header: "Queue",
  //   cell: (d) => d.queue,
  //   isRowHeader: true,
  //   sortingField: "queue",
  //   minWidth: 130
  // },
  {
    id: "callerSentimentScore",
    header: "Cust Sent",
    sortingField: "callerSentimentScore",
    cell: (d) => (
      <div className="flex items-center justify-center space-x-2">
        <SentimentIcon score={d?.callerSentimentScore} size="sm" />
        <TrendIcon trend={d.callerSentimentChange} size="sm" />
        <span className="text-sm">{Number.isFinite(d.callerSentimentScore) ? Math.floor(d.callerSentimentScore) : '-'}</span>
      </div>
    ),
    minWidth: 130
  },
  {
    id: "langCode",
    header: "Lang Code",
    sortingField: "lang",
    cell: (d) => d.lang,
    minWidth: 100,
  },
  {
    id: "duration",
    header: "Duration",
    cell: (d) => Formatter.Time(d.duration),
    minWidth: 130,
  }
];

// Module-scope so parent re-renders don't remount the modal and wipe typed input.
const EditResultModal = ({ open, callResult, callKey, email, onClose }) => {
  const [compliance, setCompliance] = useState(callResult?.Compliance ?? true);
  const [score, setScore] = useState(callResult?.QA_Score ?? 0);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    // Same shape as the Dashboard override save: { changes: [...], user }
    const payload = {
      changes: [{ compliance, score, reason }],
      user: email,
    };

    try {
      await override(callKey, payload);
      onClose();
    } catch (err) {
      console.error('Error submitting result update', err);
      window.alert('Failed to save result update. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
      <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
        <h2 className="text-lg font-bold mb-4">Edit Result</h2>

        <label className="block mb-2">
          <span className="text-gray-700">Compliance</span>
          <SelectField
            value={compliance ? 'true' : 'false'}
            onChange={(e) => setCompliance(e.target.value === 'true')}
            className="mt-1 block w-full border-gray-200 rounded-lg"
          >
            <option value="true">Compliant</option>
            <option value="false">Non-compliant</option>
          </SelectField>
        </label>

        <label className="block mb-2">
          <span className="text-gray-700">QA Score</span>
          <input
            type="number"
            value={score}
            onChange={(e) => setScore(Number(e.target.value))}
            className="mt-1 block w-full border rounded-lg border-gray-200"
            min={0}
            max={100}
          />
        </label>

        <label className="block mb-4">
          <span className="text-gray-700">Reason for Change</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="mt-1 block w-full border rounded-lg border-gray-200"
            rows={3}
          />
        </label>

        <div className="flex flex-wrap justify-end gap-2">
          <button
            onClick={onClose}
            className="btn-outline"
            disabled={submitting}
          >
            <FaTimes />
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="btn-primary"
            disabled={submitting}
          >
            <FaSave className="inline mr-2" />
            {submitting ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const getMatchesCountText = (count) => {
  return count === 1 ? `1 match` : `${count} matches`;
};

const Loading = (columns) =>
  columns.map((c, i) => (
    <td key={i} className="px-5 py-3">
      <Placeholder />
    </td>
  ));

const NoMatches = ({ children, colSpan }) => (
  <tr>
    <td colSpan={colSpan} className="px-6 py-8 text-center text-gray-500">
      {children || (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
          <p>Try adjusting your search criteria or filters.</p>
        </div>
      )}
    </td>
  </tr>
);

export const ContactTable = ({
  email,
  group,
  data = [],
  loading = false,
  empty,
  header,
  variant = 'embedded',
  onRowClick,
  assignments = {},
  users = [],
  onAssign,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false
}) => {
  const [preferences, setPreferences] = useLocalStorage(
    'contact-table-preferences',
    DEFAULT_PREFERENCES,
  );
  const navigate = useNavigate();
  const [callKey , setCallKey] =useState('')
  const [callResult, setCallResult] = useState('')
  const [sortField, setSortField] = useState(preferences.sortField);
  const [sortDirection, setSortDirection] = useState(preferences.sortDirection);
  const [currentPage, setCurrentPage] = useSessionStorage('contact-table-current-page', 1);
  const [pageSize, setPageSize] = useState(preferences.pageSize);
  const [filters] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, searchTerm]);
  
  // Handle row click
  const handleRowClick = (item) => {
    if (onRowClick) {
      onRowClick(item);
    } else {
      navigate(`/dashboard/${item.key || item.filename}`);
    }
  };

  // Handle sorting
  const handleSort = (field) => {
    let newDirection = 'asc';
    if (sortField === field && sortDirection === 'asc') {
      newDirection = 'desc';
    }
    
    setSortField(field);
    setSortDirection(newDirection);
    setCurrentPage(1);
    
    // Save preferences
    setPreferences({
      ...preferences,
      sortField: field,
      sortDirection: newDirection
    });
  };

  // Filter and sort data
  const processedData = useMemo(() => {
    let filteredData = [...data];

    // Apply search filter
    if (searchTerm) {
      filteredData = filteredData.filter(item =>
        Object.values(item).some(value =>
          String(value).toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    // Apply column filters
    Object.entries(filters).forEach(([field, filterValue]) => {
      if (filterValue) {
        filteredData = filteredData.filter(item =>
          String(item[field]).toLowerCase().includes(String(filterValue).toLowerCase())
        );
      }
    });

    // Apply sorting
    if (sortField) {
      // Resolve dotted sorting fields (e.g. "Result.QA_Score") to their nested value.
      const getSortValue = (item, field) =>
        field.includes('.')
          ? field.split('.').reduce((obj, k) => (obj == null ? obj : obj[k]), item)
          : item[field];

      // A call with no QA score (shown as "—") sorts last either way, not as a real 0.
      const noScore = sortDirection === 'asc' ? Number.MAX_SAFE_INTEGER : -Number.MAX_SAFE_INTEGER;
      const sortValueOf = (item) =>
        sortField === 'Result.QA_Score' ? (qaScoreOf(item) ?? noScore) : getSortValue(item, sortField);

      filteredData.sort((a, b) => {
        let aVal = sortValueOf(a);
        let bVal = sortValueOf(b);

        // Handle timestamps
        if (sortField === 'timestamp') {
          aVal = new Date(aVal).getTime();
          bVal = new Date(bVal).getTime();
        }

        // Primary sort comparison
        let primaryComparison = 0;

        // Handle numbers
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          primaryComparison = sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
        } else {
          // Handle strings
          aVal = String(aVal).toLowerCase();
          bVal = String(bVal).toLowerCase();

          if (sortDirection === 'asc') {
            primaryComparison = aVal.localeCompare(bVal);
          } else {
            primaryComparison = bVal.localeCompare(aVal);
          }
        }

        // If primary values are equal, sort by timestamp as secondary
        if (primaryComparison === 0 && sortField !== 'timestamp') {
          const aTime = new Date(a.timestamp).getTime();
          const bTime = new Date(b.timestamp).getTime();
          // Always sort timestamp descending (newest first) for secondary sort
          return bTime - aTime;
        }

        return primaryComparison;
      });
    }

    return filteredData;
  }, [data, searchTerm, filters, sortField, sortDirection]);

  // Pagination — clamp the stored page so a shrunken result set (e.g. after
  // filtering) never points past the last page and shows "No results found".
  const totalPages = Math.ceil(processedData.length / pageSize);
  const safePage = Math.min(currentPage, Math.max(totalPages, 1));
  const startIndex = (safePage - 1) * pageSize;
  const paginatedData = processedData.slice(startIndex, startIndex + pageSize);

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
    setPreferences({
      ...preferences,
      pageSize: newPageSize
    });
  };
  // useEffect(() => {
  //     setCallKey(data[0].key)
  //     setCallResult(data[0].Result)
  // }, [])

  const [open, setOpen] = useState(false);
  // Accepts the clicked row's item so the modal edits that call, not always
  // the first row. Callers (e.g. the Override column cell below) should invoke
  // handleOpenModal(item) after stopping event propagation themselves.
  const handleOpenModal = (item) => {
    setCallKey(item.key);
    setCallResult(item.Result);
    setOpen(true);
  };

  const columns = useMemo(() => {
    let cols = [...BASE_COLUMNS];
    // if (group) {
    //   if (group[0] == 'QAAgent') {
    //     const assignCols = [
    //       {
    //         id: 'override',
    //         header: 'Override',
    //         cell: (d) => {

    //           return (
    //             <>
    //               <button onClick={(e) => { e.stopPropagation(); handleOpenModal(d); }} className="px-4 py-2 bg-cloudza-600 text-white text-sm font-semibold rounded-full hover:bg-cloudza-700">
    //                 Override
    //               </button>
    //             </>
    //           );
    //         },
    //         sortingField: 'override',
    //         minWidth: 130,
    //       }
    //     ];
    //     cols.splice(2, 0, ...assignCols);
    //   }      
    // }

    return cols;
  }, [users, assignments, onAssign]);

  if (loading) {

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {header && (
          <div className="px-6 py-4 border-b border-gray-200">
            {header}
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                {columns.map((column) => (
                  <th key={column.id} className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">
                    {column.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {Array.from({ length: 5 }).map((_, index) => (
                <tr key={index}>
                  {Loading(columns)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {open && (
        <EditResultModal
          open={open}
          onClose={() => setOpen(false)}
          callKey={callKey}
          callResult={callResult}
          email={email}
        />
      )}
      {/* Header */}
      {header && (
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex flex-wrap items-center justify-between gap-3">
            {header}
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-500">
                {getMatchesCountText(processedData.length)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Search and filters */}
      <div className="px-6 py-4 border-b border-gray-200 bg-[#fafafc]">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder="    Search all columns..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field w-full !pl-10"
            />
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              {columns.map((column) => (
                <th
                  key={column.id}
                  className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc] cursor-pointer hover:bg-[#f3f2f7] transition-colors"
                  onClick={() => handleSort(column.sortingField || column.id)}
                  style={{ minWidth: column.minWidth }}
                >
                  <div className="flex items-center space-x-1">
                    <span>{column.header}</span>
                    {sortField === (column.sortingField || column.id) && (
                      <span className="text-cloudza-600">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {paginatedData.length === 0 ? (
              <NoMatches colSpan={columns.length}>{empty}</NoMatches>
            ) : (
              paginatedData.map((item, index) => (
                <tr
                  key={item.key || item.guid || item.id || index}
                  className="hover:bg-cloudza-50/40 cursor-pointer transition-colors"
                  onClick={() => handleRowClick(item)}
                >
                  {columns.map((column) => (
                    <td key={column.id} className="px-5 py-3 whitespace-nowrap text-sm text-gray-900 align-middle">
                      {column.cell ? column.cell(item) : item[column.id] || '-'}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {paginatedData.length > 0 && (
        <div className="bg-gray-50 px-6 py-3 flex flex-wrap items-center justify-center sm:justify-between gap-3 border-t border-gray-200">
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
            <span className="text-sm text-gray-700">
              Showing {startIndex + 1} to {Math.min(startIndex + pageSize, processedData.length)} of {processedData.length} results
            </span>
            <SelectField
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              className="select-field w-28 text-sm"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size} / page
                </option>
              ))}
            </SelectField>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, safePage - 1))}
              disabled={safePage === 1}
              className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Previous Page"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            
            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (safePage <= 3) {
                  pageNum = i + 1;
                } else if (safePage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = safePage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-1 text-sm rounded-lg ${
                      safePage === pageNum
                        ? 'bg-rewardsco-600 text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() =>
                setCurrentPage(Math.min(totalPages, safePage + 1))
              }
              disabled={safePage === totalPages}
              className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Next Page"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Load More Button */}
      {hasMore && onLoadMore && (
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 text-center">
          <button
            onClick={onLoadMore}
            disabled={isLoadingMore}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoadingMore ? (
              <>
                <InlineSpinner light className="mr-2" />
                Loading...
              </>
            ) : (
              <>
                <FaChevronDown />
                Load More Calls
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

// Table preferences component
export const ContactTablePreferences = ({ preferences, onPreferencesChange }) => {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Table Preferences</h3>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-1.5">
            Rows per Page
          </label>
          <SelectField
            value={preferences.pageSize}
            onChange={(e) => onPreferencesChange({
              ...preferences,
              pageSize: Number(e.target.value)
            })}
            className="select-field"
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </SelectField>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-1.5">
            Default Sort Field
          </label>
          <SelectField
            value={preferences.sortField}
            onChange={(e) => onPreferencesChange({
              ...preferences,
              sortField: e.target.value
            })}
            className="select-field"
          >
            {BASE_COLUMNS.map(column => (
              <option key={column.id} value={column.id}>
                {column.header}
              </option>
            ))}
          </SelectField>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-1.5">
            Default Sort Direction
          </label>
          <SelectField
            value={preferences.sortDirection}
            onChange={(e) => onPreferencesChange({
              ...preferences,
              sortDirection: e.target.value
            })}
            className="select-field"
          >
            <option value="asc">Ascending</option>
            <option value="desc">Descending</option>
          </SelectField>
        </div>

        <div className="space-y-2">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={preferences.wrapLines}
              onChange={(e) => onPreferencesChange({
                ...preferences,
                wrapLines: e.target.checked
              })}
              className="rounded-lg border-gray-200 text-rewardsco-600 focus:ring-rewardsco-500"
            />
            <span className="ml-2 text-sm text-gray-700">Wrap long text</span>
          </label>
          
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={preferences.stripedRows}
              onChange={(e) => onPreferencesChange({
                ...preferences,
                stripedRows: e.target.checked
              })}
              className="rounded-lg border-gray-200 text-cyan-600 focus:ring-cyan-500"
            />
            <span className="ml-2 text-sm text-gray-700">Striped rows</span>
          </label>
        </div>
      </div>
    </div>
  );
};

export default ContactTable;